import React, { useState, useCallback, useEffect } from 'react';
import { useOrdersWebSocket } from '../hooks/useOrdersWebSocket';

/**
 * Монтируется один раз в AdminLayout.
 * Показывает:
 *  - индикатор подключения WS в шапке
 *  - тост-уведомления при появлении нового заказа
 */
export default function WsOrderNotifier({ onOrderEvent }) {
  const [toasts, setToasts] = useState([]);

  const handleMessage = useCallback((event) => {
    // Прокидываем событие наверх (например, для обновления таблицы заказов)
    onOrderEvent?.(event);

    if (event.type === 'order.created') {
      const order = event.order;
      const id = Date.now();
      setToasts((prev) => [
        ...prev,
        {
          id,
          title: `Новый заказ #${order.order_number}`,
          body: `Сумма: ${order.total_price} ₽ · ${order.order_type === 'in_place' ? 'В зале' : 'С собой'}`,
        },
      ]);
      // Автоудаление через 6 секунд
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 6000);
    }
  }, [onOrderEvent]);

  const { connected, reconnecting } = useOrdersWebSocket(handleMessage);

  return (
    <>
      {/* Индикатор статуса WS */}
      <WsIndicator connected={connected} reconnecting={reconnecting} />

      {/* Тост-стек */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            body={t.body}
            onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
          />
        ))}
      </div>
    </>
  );
}

function WsIndicator({ connected, reconnecting }) {
  if (connected) {
    return (
      <span className="flex items-center gap-1 text-xs text-green-600 font-medium" title="WebSocket подключён">
        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        Live
      </span>
    );
  }
  if (reconnecting) {
    return (
      <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium" title="Переподключение...">
        <span className="h-2 w-2 rounded-full bg-yellow-400 animate-pulse" />
        Reconnecting…
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-red-500 font-medium" title="WebSocket отключён">
      <span className="h-2 w-2 rounded-full bg-red-500" />
      Offline
    </span>
  );
}

function Toast({ title, body, onClose }) {
  useEffect(() => {
    // Браузерное уведомление если разрешено
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    }
  }, [title, body]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 flex gap-3 items-start animate-slide-in">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-lg">
        🍔
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{body}</p>
      </div>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 text-lg leading-none flex-shrink-0"
      >
        ×
      </button>
    </div>
  );
}
